import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import { getAllExploreRows, getAllContent, getAllTags, getAllStyles, getAllSections, getAllPromptsAdmin, getHomePage } from '../graphql/queries'
import { createExploreRow, createContent, reorderExploreRows } from '../graphql/mutation'
import {
  Typography,
  Box,
  Button,
  Paper,
  Tab,
  Tabs,
  Alert,
  Chip,
  Grid,
  Card,
  CardContent,
  CardActions,
  Avatar,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  FormControlLabel,
  Checkbox
} from '@mui/material'
import ExploreIcon from '@mui/icons-material/Explore'
import AddIcon from '@mui/icons-material/Add'
import WarningIcon from '@mui/icons-material/Warning'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ImageIcon from '@mui/icons-material/Image'
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary'
import EditIcon from '@mui/icons-material/Edit'
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd'
import BrushIcon from '@mui/icons-material/Brush'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import ViewModuleIcon from '@mui/icons-material/ViewModule'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import AppleIcon from '@mui/icons-material/Apple'
import AndroidIcon from '@mui/icons-material/Android'
import { uploadToS3 } from '../utils/uploadToS3'
import ContentPage from './contentPage'
import ExploreRowEditModal from '../components/ExploreRowEditModal'

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`explore-tabpanel-${index}`}
      aria-labelledby={`explore-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  )
}

export default function ExplorePageManagement() {
  const navigate = useNavigate()
  const [tabValue, setTabValue] = useState(0)
  const [selectedTag, setSelectedTag] = useState('')
  const [contentTypeFilter, setContentTypeFilter] = useState('')
  const [platformFilter, setPlatformFilter] = useState('')
  
  // Content Assets filters
  const [contentAssetsSearchTerm, setContentAssetsSearchTerm] = useState('')
  const [contentAssetsTypeFilter, setContentAssetsTypeFilter] = useState('')
  const [contentAssetsDesignTypeFilter, setContentAssetsDesignTypeFilter] = useState('')
  
  // Modal states
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedContent, setSelectedContent] = useState(null)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [selectedRowId, setSelectedRowId] = useState(null)
  const [customAssets, setCustomAssets] = useState({
    banner: '',
    secondBanner: '',
    videoUrl: '',
    gifUrl: '',
    webmUrl: '',
    webpUrl: '',
    designType: 'MEDIUM',
    rowNumber: 1,
    platform: ['IOS', 'ANDROID']
  })

  const [selectedFiles, setSelectedFiles] = useState({
    banner: null,
    secondBanner: null,
    video: null,
    gif: null,
    webm: null,
    webp: null
  })
  const [progress, setProgress] = useState(false)
  const [snackBarOpen, setSnackBarOpen] = useState(false)
  const [snackBarMessage, setSnackBarMessage] = useState('')
  const [snackBarSeverity, setSnackBarSeverity] = useState('success')

  const { data: exploreRowsData, loading: exploreLoading } = useQuery(getAllExploreRows, {
    fetchPolicy: 'network-only'
  })
  const { data: contentData, loading: contentLoading } = useQuery(getAllContent)
  const { data: tagsData, loading: tagsLoading } = useQuery(getAllTags)
  const { data: stylesData, loading: stylesLoading } = useQuery(getAllStyles, {
    variables: { pageSize: 1000, page: 1 }
  })
  const { data: sectionsData, loading: sectionsLoading } = useQuery(getAllSections)
  const { data: promptsData, loading: promptsLoading } = useQuery(getAllPromptsAdmin, {
    variables: { pageSize: 1000, page: 1 }
  })
  const { data: homePageData, loading: homePageLoading } = useQuery(getHomePage, {
    variables: { tagId: selectedTag },
    skip: !selectedTag
  })

  const [createExploreRowMutation] = useMutation(createExploreRow)
  const [createContentMutation] = useMutation(createContent)
  const [reorderExploreRowsMutation] = useMutation(reorderExploreRows)

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  const handleDragEnd = async (result) => {
    if (!result.destination || !selectedTag) {
      return
    }

    const sourceIndex = result.source.index
    const destinationIndex = result.destination.index

    if (sourceIndex === destinationIndex) {
      return
    }

    // Get current explore rows for the selected tag AND platform filter
    const currentRows = [...exploreRows.filter(row => {
      if (row.tagId !== selectedTag) return false
      
      // If platform filter is active, only include rows for that platform
      if (platformFilter) {
        const platforms = row.platform || []
        return platforms.includes(platformFilter)
      }
      
      return true
    })].sort((a, b) => a.order - b.order)
    
    // Reorder the array
    const reorderedRows = Array.from(currentRows)
    const [movedRow] = reorderedRows.splice(sourceIndex, 1)
    reorderedRows.splice(destinationIndex, 0, movedRow)

    // Create updates with new order values
    const updates = reorderedRows.map((row, index) => ({
      id: row.id,
      order: index + 1
    }))

    try {
      await reorderExploreRowsMutation({
        variables: { updates },
        refetchQueries: [{ query: getAllExploreRows }]
      })
      
      const platformMsg = platformFilter ? ` (${platformFilter} only)` : ''
      setSnackBarMessage(`Order updated successfully${platformMsg}!`)
      setSnackBarSeverity('success')
      setSnackBarOpen(true)
    } catch (error) {
      setSnackBarMessage('Failed to update order')
      setSnackBarSeverity('error')
      setSnackBarOpen(true)
    }
  }

  const handleContentSelect = (contentItem, contentType) => {
    if (!selectedTag) {
      setSnackBarMessage('Please select a tag first before adding content to explore')
      setSnackBarSeverity('warning')
      setSnackBarOpen(true)
      return
    }

    // Pre-populate custom assets fields with existing content assets
    const prefilledAssets = {
      banner: contentItem.banner || '',
      secondBanner: contentItem.secondBanner || '',
      videoUrl: contentItem.videoUrl || '',
      gifUrl: contentItem.gifUrl || '',
      webmUrl: contentItem.webmUrl || '',
      webpUrl: contentItem.webpUrl || '',
      designType: contentItem.designType || 'MEDIUM',
      rowNumber: 1
    }

    setSelectedContent({ ...contentItem, contentType })
    setCustomAssets(prefilledAssets)
    setModalOpen(true)
  }

  const handleAddWithDefaults = async () => {
    if (!selectedContent || !selectedTag) return

    setProgress(true)
    try {
      // Create explore row with default assets
      await createExploreRowMutation({
        variables: {
          order: exploreRows.length + 1,
          isHighlighted: false,
          contentId: selectedContent.id,
          contentType: selectedContent.contentType,
          platform: customAssets.platform,
          tagId: selectedTag
        },
        refetchQueries: [
          { query: getAllExploreRows },
          { query: getAllContent },
          { query: getHomePage, variables: { tagId: selectedTag } }
        ]
      })

      setSnackBarMessage('Content added to explore with default assets!')
      setSnackBarSeverity('success')
      setSnackBarOpen(true)
      setModalOpen(false)
    } catch (error) {
      setSnackBarMessage(`Error: ${error.message}`)
      setSnackBarSeverity('error')
      setSnackBarOpen(true)
    } finally {
      setProgress(false)
    }
  }

  const handleAddWithCustomAssets = async () => {
    if (!selectedContent || !selectedTag) return

    setProgress(true)
    try {
      let finalBannerUrl = customAssets.banner
      let finalSecondBannerUrl = customAssets.secondBanner
      let finalVideoUrl = customAssets.videoUrl
      let finalGifUrl = customAssets.gifUrl
      let finalWebmUrl = customAssets.webmUrl
      let finalWebpUrl = customAssets.webpUrl

      // Upload banner file if selected
      if (selectedFiles.banner) {
        setSnackBarMessage('Uploading banner image...')
        setSnackBarSeverity('info')
        setSnackBarOpen(true)
        
        try {
          finalBannerUrl = await uploadToS3(selectedFiles.banner, 'img')
        } catch (uploadError) {
          throw new Error(`Failed to upload banner: ${uploadError.message}`)
        }
      }

      // Upload second banner file if selected
      if (selectedFiles.secondBanner) {
        setSnackBarMessage('Uploading second banner image...')
        setSnackBarSeverity('info')
        setSnackBarOpen(true)
        
        try {
          finalSecondBannerUrl = await uploadToS3(selectedFiles.secondBanner, 'img')
        } catch (uploadError) {
          throw new Error(`Failed to upload second banner: ${uploadError.message}`)
        }
      }

      // Upload video file if selected
      if (selectedFiles.video) {
        setSnackBarMessage('Uploading video...')
        setSnackBarSeverity('info')
        setSnackBarOpen(true)
        
        try {
          finalVideoUrl = await uploadToS3(selectedFiles.video, 'video')
        } catch (uploadError) {
          throw new Error(`Failed to upload video: ${uploadError.message}`)
        }
      }

      // Upload gif file if selected
      if (selectedFiles.gif) {
        setSnackBarMessage('Uploading GIF...')
        setSnackBarSeverity('info')
        setSnackBarOpen(true)
        
        try {
          finalGifUrl = await uploadToS3(selectedFiles.gif, 'gif')
        } catch (uploadError) {
          throw new Error(`Failed to upload GIF: ${uploadError.message}`)
        }
      }

      // Upload webm file if selected
      if (selectedFiles.webm) {
        setSnackBarMessage('Uploading WEBM video...')
        setSnackBarSeverity('info')
        setSnackBarOpen(true)
        
        try {
          finalWebmUrl = await uploadToS3(selectedFiles.webm, 'video')
        } catch (uploadError) {
          throw new Error(`Failed to upload WEBM: ${uploadError.message}`)
        }
      }

      // Upload webp file if selected
      if (selectedFiles.webp) {
        setSnackBarMessage('Uploading WEBP image...')
        setSnackBarSeverity('info')
        setSnackBarOpen(true)
        
        try {
          finalWebpUrl = await uploadToS3(selectedFiles.webp, 'img')
        } catch (uploadError) {
          throw new Error(`Failed to upload WEBP: ${uploadError.message}`)
        }
      }

      // First create custom content assets
      await createContentMutation({
        variables: {
          contentId: selectedContent.id, // Use the selected content's ID (prompt/style/section)
          contentType: selectedContent.contentType, // Use the actual content type (PROMPT/STYLE/SECTION)
          designType: customAssets.designType,
          banner: finalBannerUrl || null,
          secondBanner: finalSecondBannerUrl || null,
          videoUrl: finalVideoUrl || null,
          gifUrl: finalGifUrl || null,
          webmUrl: finalWebmUrl || null,
          webpUrl: finalWebpUrl || null,
          rowNumber: customAssets.rowNumber,
          tagId: selectedTag, // Link this content to the selected tag for explore
          // sectionId, styleId, promptId undefined - let backend use automatic logic
        }
      })

      // Then create the explore row
      await createExploreRowMutation({
        variables: {
          order: exploreRows.length + 1,
          isHighlighted: false,
          contentId: selectedContent.id,
          contentType: selectedContent.contentType,
          platform: customAssets.platform,
          tagId: selectedTag
        },
        refetchQueries: [
          { query: getAllExploreRows },
          { query: getAllContent },
          { query: getHomePage, variables: { tagId: selectedTag } }
        ]
      })

      setSnackBarMessage('Content added to explore with custom assets!')
      setSnackBarSeverity('success')
      setSnackBarOpen(true)
      setModalOpen(false)
      setCustomAssets({ banner: '', secondBanner: '', videoUrl: '', gifUrl: '', webmUrl: '', webpUrl: '', designType: 'MEDIUM', rowNumber: 1, platform: ['IOS', 'ANDROID'] })
      setSelectedFiles({ banner: null, secondBanner: null, video: null, gif: null, webm: null, webp: null })
    } catch (error) {
      setSnackBarMessage(`Error: ${error.message}`)
      setSnackBarSeverity('error')
      setSnackBarOpen(true)
    } finally {
      setProgress(false)
    }
  }

  const handleCloseModal = () => {
    setModalOpen(false)
    setSelectedContent(null)
    setCustomAssets({ banner: '', secondBanner: '', videoUrl: '', gifUrl: '', webmUrl: '', webpUrl: '', designType: 'MEDIUM', rowNumber: 1, platform: ['IOS', 'ANDROID'] })
    setSelectedFiles({ banner: null, secondBanner: null, video: null, gif: null, webm: null, webp: null })
  }

  const handleFileSelect = (type, event) => {
    const file = event.target.files[0]
    if (file) {
      setSelectedFiles(prev => ({ ...prev, [type]: file }))
    }
  }



  if (exploreLoading || contentLoading || tagsLoading || stylesLoading || sectionsLoading || promptsLoading) {
    return <div>Loading...</div>
  }

  const exploreRows = exploreRowsData?.getAllExploreRows || []
  const content = contentData?.getAllContent || []
  const tags = tagsData?.getAllTags || []
  const styles = stylesData?.getAllStylesAdmin?.styles || []
  const sections = sectionsData?.getAllSections || []
  const prompts = promptsData?.getAllPromptsAdmin?.prompts || []
  
  // Debug: Log data
  console.log('Tags data:', tags)
  console.log('Tags count:', tags.length)
  console.log('Explore Management - All rows:', exploreRows)
  console.log('Explore Management - Platform filter:', platformFilter)
  if (exploreRows.length > 0) {
    console.log('Explore Management - First row platform:', exploreRows[0].platform)
  }

  // Helper function to get assets for an explore row
  const getRowAssets = (row) => {
    const { contentId, contentType } = row
    let assets = { banner: null, videoUrl: null, designType: null, name: null }
    
    switch (contentType) {
      case 'STYLE':
        const style = styles.find(s => s.id === contentId)
        if (style) {
          assets = {
            banner: style.banner,
            videoUrl: style.videoUrl || style.gifUrl,
            designType: style.designType,
            name: style.name
          }
        }
        break
      case 'SECTION':
        const section = sections.find(s => s.id === contentId)
        if (section) {
          assets = {
            banner: section.banner,
            videoUrl: section.videoUrl || section.gifUrl,
            designType: section.designType,
            name: section.name
          }
        }
        break
      case 'PROMPT':
        const prompt = prompts.find(p => p.id === contentId)
        if (prompt) {
          assets = {
            banner: prompt.banner,
            videoUrl: prompt.videoUrl,
            designType: prompt.designType,
            name: prompt.name || 'Untitled Prompt'
          }
        }
        break
    }
    
    // Check for custom content override
    const customContent = content.find(c => 
      c.contentId === contentId && 
      c.contentType === contentType && 
      c.tagId === row.tagId
    )
    
    if (customContent) {
      return {
        ...assets,
        banner: customContent.banner || assets.banner,
        videoUrl: customContent.videoUrl || assets.videoUrl,
        designType: customContent.designType || assets.designType,
        isCustom: true
      }
    }
    
    return { ...assets, isCustom: false }
  }

  // Combine all content for the "Add Content" tab
  const allAvailableContent = [
    ...(Array.isArray(styles) ? styles.map(item => ({ ...item, contentType: 'STYLE', icon: BrushIcon })) : []),
    ...(Array.isArray(sections) ? sections.map(item => ({ ...item, contentType: 'SECTION', icon: ViewModuleIcon })) : []),
    ...(Array.isArray(prompts) ? prompts.map(item => ({ ...item, contentType: 'PROMPT', icon: ChatBubbleOutlineIcon })) : [])
  ]

  // Group explore rows by tag
  const exploreRowsByTag = exploreRows.reduce((acc, row) => {
    const tagId = row.tagId
    if (!acc[tagId]) acc[tagId] = []
    acc[tagId].push(row)
    return acc
  }, {})

  // Group content by tag
  const contentByTag = content.reduce((acc, item) => {
    const tagId = item.tagId
    if (tagId) {
      if (!acc[tagId]) acc[tagId] = []
      acc[tagId].push(item)
    }
    return acc
  }, {})

  // Check which tags have explore rows but no content assets
  const getTagStatus = (tagId) => {
    const hasExploreRows = exploreRowsByTag[tagId]?.length > 0
    const hasContent = contentByTag[tagId]?.length > 0
    
    if (hasExploreRows && hasContent) return 'complete'
    if (hasExploreRows && !hasContent) return 'needs-assets'
    if (!hasExploreRows && hasContent) return 'unused-assets'
    return 'empty'
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'complete': return 'success'
      case 'needs-assets': return 'warning'
      case 'unused-assets': return 'info'
      default: return 'default'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'complete': return <CheckCircleIcon />
      case 'needs-assets': return <WarningIcon />
      default: return null
    }
  }

  const getStatusText = (status) => {
    switch (status) {
      case 'complete': return 'Ready for Explore'
      case 'needs-assets': return 'Needs Content Assets'
      case 'unused-assets': return 'Assets Without Explore Rows'
      default: return 'No Data'
    }
  }

  const filteredTags = selectedTag 
    ? tags.filter(tag => tag.id === selectedTag)
    : tags

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        maxWidth: '1400px',
        mx: 'auto',
        px: 2,
        py: 2,
      }}
    >
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 2
      }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ExploreIcon fontSize="large" sx={{ color: '#1976d2' }} />
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
            Explore Page Management
          </Typography>
        </Box>

      </Box>

      {/* Description */}
      <Alert severity="info">
        <Typography variant="body2">
          <strong>Explore Page System:</strong> Manage your explore page by creating explore rows (structure) and content assets (visuals). 
          Each explore row requires a tag and should have corresponding content assets for complete functionality.
        </Typography>
      </Alert>

      {/* Filter */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Tag</InputLabel>
          <Select
            value={selectedTag}
            label="Filter by Tag"
            onChange={(e) => setSelectedTag(e.target.value)}
          >
            <MenuItem value="">All Tags</MenuItem>
            {tags.map((tag) => (
              <MenuItem key={tag.id} value={tag.id}>
                {tag.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Platform Filter */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
            Platform:
          </Typography>
          <Chip
            icon={<AppleIcon />}
            label="iOS"
            onClick={() => setPlatformFilter(platformFilter === 'IOS' ? '' : 'IOS')}
            color={platformFilter === 'IOS' ? 'default' : 'default'}
            variant={platformFilter === 'IOS' ? 'filled' : 'outlined'}
            sx={{
              backgroundColor: platformFilter === 'IOS' ? '#000' : 'transparent',
              color: platformFilter === 'IOS' ? '#fff' : '#000',
              borderColor: platformFilter === 'IOS' ? '#000' : '#ccc',
              '& .MuiChip-icon': { 
                color: platformFilter === 'IOS' ? '#fff' : '#000' 
              },
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: platformFilter === 'IOS' ? '#333' : '#f5f5f5'
              }
            }}
          />
          <Chip
            icon={<AndroidIcon />}
            label="Android"
            onClick={() => setPlatformFilter(platformFilter === 'ANDROID' ? '' : 'ANDROID')}
            color={platformFilter === 'ANDROID' ? 'default' : 'default'}
            variant={platformFilter === 'ANDROID' ? 'filled' : 'outlined'}
            sx={{
              backgroundColor: platformFilter === 'ANDROID' ? '#3DDC84' : 'transparent',
              color: platformFilter === 'ANDROID' ? '#000' : '#000',
              borderColor: platformFilter === 'ANDROID' ? '#3DDC84' : '#ccc',
              '& .MuiChip-icon': { 
                color: '#000'
              },
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: platformFilter === 'ANDROID' ? '#2CCC73' : '#f5f5f5'
              }
            }}
          />
          {platformFilter && (
            <Button
              size="small"
              onClick={() => setPlatformFilter('')}
              sx={{ minWidth: 'auto', p: 0.5 }}
            >
              Clear
            </Button>
          )}
        </Box>
      </Box>

      {/* Tabs */}
      <Paper elevation={2} sx={{ borderRadius: 2 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Tag Overview" />
          <Tab label="Explore Rows" />
          <Tab label="Content Assets" />
          <Tab label="Add Content" />
          <Tab label="Home Page Preview" />
        </Tabs>

        {/* Tab 1: Tag Overview */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            {filteredTags.map((tag) => {
              const status = getTagStatus(tag.id)
              const exploreCount = exploreRowsByTag[tag.id]?.length || 0
              const contentCount = contentByTag[tag.id]?.length || 0
              
              return (
                <Grid item xs={12} sm={6} md={4} key={tag.id}>
                  <Card 
                    elevation={1}
                    sx={{ 
                      height: '100%',
                      border: status === 'needs-assets' ? '2px solid #ff9800' : '1px solid #e0e0e0'
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {tag.name}
                        </Typography>
                        {getStatusIcon(status)}
                      </Box>
                      
                      <Chip 
                        label={getStatusText(status)}
                        color={getStatusColor(status)}
                        size="small"
                        sx={{ mb: 2 }}
                      />
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Explore Rows:
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {exploreCount}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">
                          Content Assets:
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {contentCount}
                        </Typography>
                      </Box>
                    </CardContent>
                    
                    <CardActions>
                      <Button 
                        size="small"
                        onClick={() => navigate(`/content/create?tagId=${tag.id}`)}
                      >
                        Add Content Asset
                      </Button>
                      <Button 
                        size="small"
                        onClick={() => {
                          setSelectedTag(tag.id)
                          setTabValue(3) // Go to Add Content tab
                        }}
                        variant="outlined"
                      >
                        Add to Explore
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              )
            })}
          </Grid>
        </TabPanel>

        {/* Tab 2: Explore Rows */}
        <TabPanel value={tabValue} index={1}>
          {!selectedTag && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Select a tag above to view its explore rows and drag to reorder them.
            </Alert>
          )}
          
          {selectedTag && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Explore Rows for {tags.find(t => t.id === selectedTag)?.name || 'Selected Tag'}
                  {platformFilter && ` (Showing ${platformFilter} only)`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  💡 Use the "Add Content" tab to add new explore rows
                </Typography>
              </Box>
              
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  💡 <strong>Drag & Drop:</strong> Drag the rows by the handle to reorder them. Changes will be saved automatically.
                  {platformFilter && (
                    <span> You are reordering <strong>{platformFilter}</strong> rows only - other platforms will not be affected.</span>
                  )}
                </Typography>
              </Alert>
              
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="explore-rows">
                  {(provided) => (
                    <Box
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                    >
                      {(() => {
                        const filteredRows = [...exploreRows].filter(row => {
                          console.log('Check row:', row.id, 'tagId:', row.tagId, 'selectedTag:', selectedTag, 'match:', row.tagId === selectedTag)
                          if (row.tagId !== selectedTag) return false
                          if (platformFilter) {
                            const platforms = row.platform || []
                            const includes = platforms.includes(platformFilter)
                            console.log('Filtering row:', row.id, 'platform:', platforms, 'filter:', platformFilter, 'includes:', includes)
                            return includes
                          }
                          return true
                        }).sort((a, b) => a.order - b.order)
                        
                        console.log('After filtering, showing', filteredRows.length, 'rows')
                        
                        return filteredRows.map((row, index) => {
                          console.log('Rendering row:', row.id, 'platform:', row.platform)
                          const tag = tags.find(t => t.id === row.tagId)
                          const hasAssets = contentByTag[row.tagId]?.length > 0
                          const assets = getRowAssets(row)
                          
                          return (
                            <Draggable key={row.id} draggableId={row.id} index={index}>
                              {(provided, snapshot) => (
                                <Card
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  elevation={snapshot.isDragging ? 8 : 1}
                                  sx={{
                                    transform: snapshot.isDragging ? 'rotate(2deg)' : 'none',
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': { 
                                      elevation: 3,
                                      transform: snapshot.isDragging ? 'rotate(2deg)' : 'translateY(-1px)',
                                    },
                                    bgcolor: snapshot.isDragging ? 'action.hover' : 'background.paper'
                                  }}
                                >
                                  <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                      {/* Drag Handle */}
                                      <Box
                                        {...provided.dragHandleProps}
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          color: 'text.secondary',
                                          cursor: 'grab',
                                          '&:active': { cursor: 'grabbing' },
                                          '&:hover': { color: 'primary.main' }
                                        }}
                                      >
                                        <DragIndicatorIcon />
                                      </Box>
                                      
                                      {/* Assets Preview */}
                                      {assets.banner && (
                                        <Box sx={{ width: 120, height: 80, mr: 2, flexShrink: 0 }}>
                                          <img
                                            src={assets.banner}
                                            alt={assets.name || 'Asset'}
                                            style={{
                                              width: '100%',
                                              height: '100%',
                                              objectFit: 'cover',
                                              borderRadius: '8px',
                                              border: '1px solid #e0e0e0'
                                            }}
                                            onError={(e) => {
                                              e.target.style.display = 'none'
                                            }}
                                          />
                                        </Box>
                                      )}
                                      
                                      {/* Content */}
                                      <Box sx={{ flex: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                            <Typography variant="h6">#{row.order}</Typography>
                                            <Chip label={row.contentType} color="primary" size="small" />
                                            {row.isHighlighted && <Chip label="Highlighted" color="warning" size="small" />}
                                            {assets.isCustom && <Chip label="Custom Assets" color="info" size="small" />}
                                            {row.platform?.includes('IOS') && (
                                              <Chip
                                                icon={<AppleIcon />}
                                                label="iOS"
                                                size="small"
                                                sx={{
                                                  backgroundColor: '#000',
                                                  color: '#fff',
                                                  '& .MuiChip-icon': { color: '#fff' }
                                                }}
                                              />
                                            )}
                                            {row.platform?.includes('ANDROID') && (
                                              <Chip
                                                icon={<AndroidIcon />}
                                                label="Android"
                                                size="small"
                                                sx={{
                                                  backgroundColor: '#3DDC84',
                                                  color: '#000',
                                                  '& .MuiChip-icon': { color: '#000' }
                                                }}
                                              />
                                            )}
                                          </Box>
                                          <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<EditIcon />}
                                            onClick={() => {
                                              setSelectedRowId(row.id)
                                              setEditModalOpen(true)
                                            }}
                                          >
                                            Edit
                                          </Button>
                                        </Box>
                                        
                                        {/* Asset Info */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                          <Typography variant="body1" fontWeight="medium">
                                            {assets.name || 'Unnamed Content'}
                                          </Typography>
                                          <Chip 
                                            label={assets.designType || 'MEDIUM'} 
                                            color="secondary" 
                                            size="small" 
                                            variant="outlined"
                                          />
                                        </Box>
                                        
                                        {/* Asset Status */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                          <Typography variant="body2">Tag: {tag?.name || 'Unknown Tag'}</Typography>
                                          {assets.banner && (
                                            <Chip 
                                              icon={<CheckCircleIcon />} 
                                              label="Has Banner" 
                                              color="success" 
                                              size="small" 
                                            />
                                          )}
                                          {assets.videoUrl && (
                                            <Chip 
                                              icon={<CheckCircleIcon />} 
                                              label="Has Video" 
                                              color="success" 
                                              size="small" 
                                            />
                                          )}
                                          {!assets.banner && !assets.videoUrl && (
                                            <Chip 
                                              icon={<WarningIcon />} 
                                              label="No Assets" 
                                              color="warning" 
                                              size="small" 
                                            />
                                          )}
                                        </Box>
                                        
                                        <Typography variant="body2" color="text.secondary">
                                          Content ID: {row.contentId.substring(0, 30)}...
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </CardContent>
                                </Card>
                                )}
                            </Draggable>
                          )
                        })
                      })()}
                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>
              </DragDropContext>
              
              {exploreRows.filter(row => row.tagId === selectedTag).length === 0 && (
                <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                  <Typography variant="h6" gutterBottom>
                    No explore rows found for this tag
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Go to the "Add Content" tab to add your first explore row.
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => setTabValue(3)}
                    sx={{ mt: 1 }}
                  >
                    Go to Add Content Tab
                  </Button>
                </Paper>
              )}
            </Box>
          )}
          
          {!selectedTag && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[...exploreRows]
                .sort((a, b) => a.order - b.order)
                .map((row) => {
                  const tag = tags.find(t => t.id === row.tagId)
                  const hasAssets = contentByTag[row.tagId]?.length > 0
                  const assets = getRowAssets(row)
                  
                  return (
                    <Card key={row.id} elevation={1}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          {/* Assets Preview */}
                          {assets.banner && (
                            <Box sx={{ width: 100, height: 70, flexShrink: 0 }}>
                              <img
                                src={assets.banner}
                                alt={assets.name || 'Asset'}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  borderRadius: '6px',
                                  border: '1px solid #e0e0e0'
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none'
                                }}
                              />
                            </Box>
                          )}
                          
                          {/* Content */}
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography variant="h6">#{row.order}</Typography>
                                <Chip label={row.contentType} color="primary" size="small" />
                                {row.isHighlighted && <Chip label="Highlighted" color="warning" size="small" />}
                                {assets.isCustom && <Chip label="Custom Assets" color="info" size="small" />}
                              </Box>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<EditIcon />}
                                onClick={() => navigate(`/explore-rows/${row.id}`)}
                              >
                                Edit
                              </Button>
                            </Box>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                              <Typography variant="body1" fontWeight="medium">
                                {assets.name || 'Unnamed Content'}
                              </Typography>
                              <Chip 
                                label={assets.designType || 'MEDIUM'} 
                                color="secondary" 
                                size="small" 
                                variant="outlined"
                              />
                            </Box>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Typography variant="body2">Tag: {tag?.name || 'Unknown Tag'}</Typography>
                              {assets.banner && (
                                <Chip 
                                  icon={<CheckCircleIcon />} 
                                  label="Banner" 
                                  color="success" 
                                  size="small" 
                                />
                              )}
                              {assets.videoUrl && (
                                <Chip 
                                  icon={<CheckCircleIcon />} 
                                  label="Video" 
                                  color="success" 
                                  size="small" 
                                />
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  )
                })}
            </Box>
          )}
        </TabPanel>

        {/* Tab 3: Content Assets */}
        <TabPanel value={tabValue} index={2}>
          <ContentPage />
        </TabPanel>

        {/* Tab 4: Add Content */}
        <TabPanel value={tabValue} index={3}>
          {!selectedTag && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Please select a tag first to add content to explore rows.
            </Alert>
          )}
          
          {/* Content Type Filter */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Filter by Content Type</InputLabel>
              <Select
                value={contentTypeFilter}
                label="Filter by Content Type"
                onChange={(e) => setContentTypeFilter(e.target.value)}
              >
                <MenuItem value="">All Content Types</MenuItem>
                <MenuItem value="STYLE">Styles</MenuItem>
                <MenuItem value="SECTION">Sections</MenuItem>
                <MenuItem value="PROMPT">Prompts</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary">
              Total items: {allAvailableContent.filter(item => 
                !contentTypeFilter || item.contentType === contentTypeFilter
              ).length}
            </Typography>
          </Box>
          
          <TableContainer component={Paper} elevation={1}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allAvailableContent
                  .filter(item => {
                    // Show items that have names, or prompts that have prompt text
                    return item.name || (item.contentType === 'PROMPT' && item.prompt)
                  })
                  .filter(item => !contentTypeFilter || item.contentType === contentTypeFilter) // Filter by content type
                  .map((item) => {
                    const IconComponent = item.icon
                    return (
                      <TableRow key={`${item.contentType}-${item.id}`}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconComponent fontSize="small" />
                            <Chip 
                              label={item.contentType} 
                              size="small" 
                              color={
                                item.contentType === 'STYLE' ? 'secondary' :
                                item.contentType === 'SECTION' ? 'success' : 'primary'
                              }
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {item.contentType === 'PROMPT' 
                              ? (item.name || 'Untitled Prompt')
                              : item.name
                            }
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {item.contentType === 'PROMPT' 
                              ? (item.prompt?.length > 100 ? item.prompt.substring(0, 100) + '...' : item.prompt || 'No prompt text')
                              : (item.description || 'No description available')
                            }
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<PlaylistAddIcon />}
                            onClick={() => handleContentSelect(item, item.contentType)}
                            disabled={!selectedTag}
                          >
                            Add to Explore
                          </Button>
                        </TableCell>
                      </TableRow>
                    )
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Tab 5: Home Page Preview */}
        <TabPanel value={tabValue} index={4}>
          {!selectedTag && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Select a tag above to preview how the home page will look for that tag.
            </Alert>
          )}
          
          {selectedTag && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Home Page Preview for {tags.find(t => t.id === selectedTag)?.name || 'Selected Tag'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  📱 This is exactly how your mobile app will display the content
                </Typography>
              </Box>
              
              {homePageLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : homePageData?.getHomePage?.home?.length > 0 ? (
                <Grid container spacing={2}>
                  {homePageData.getHomePage.home.map((item, index) => (
                    <Grid item xs={12} key={item.id}>
                      <Card 
                        elevation={2}
                        sx={{ 
                          border: item.orderAndHighlight?.highlighted ? '2px solid #ff9800' : '1px solid #e0e0e0',
                          backgroundColor: item.orderAndHighlight?.highlighted ? '#fff3e0' : 'white'
                        }}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                              #{index + 1} - {item.sectionName || item.styleName || item.promptName || 'Untitled'}
                            </Typography>
                            {item.orderAndHighlight?.highlighted && (
                              <Chip 
                                label="Highlighted" 
                                color="warning" 
                                size="small"
                                icon={<CheckCircleIcon />}
                              />
                            )}
                            <Chip 
                              label={`Order: ${item.orderAndHighlight?.order || 'N/A'}`} 
                              variant="outlined" 
                              size="small"
                            />
                          </Box>
                          
                          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            <Chip 
                              label={item.sectionName ? 'Section' : item.styleName ? 'Style' : 'Prompt'} 
                              color="primary" 
                              size="small"
                            />
                            {item.toolType && (
                              <Chip label={item.toolType} color="secondary" size="small" />
                            )}
                          </Box>

                          {/* Main Content Preview - Mobile-like */}
                          {item.assets && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Main Content:
                              </Typography>
                              <Card 
                                sx={{ 
                                  width: '100%',
                                  maxWidth: 400,
                                  border: '1px solid #e0e0e0',
                                  borderRadius: 2,
                                  overflow: 'hidden'
                                }}
                              >
                                <Box 
                                  sx={{ 
                                    height: 200, 
                                    backgroundColor: '#f5f5f5',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    position: 'relative'
                                  }}
                                >
                                  {item.assets.banner ? (
                                    <img 
                                      src={item.assets.banner} 
                                      alt={item.sectionName || item.styleName || item.promptName || 'Content'}
                                      style={{ 
                                        width: '100%', 
                                        height: '100%', 
                                        objectFit: 'cover' 
                                      }}
                                    />
                                  ) : (
                                    <ImageIcon sx={{ fontSize: 60, color: '#ccc' }} />
                                  )}
                                  {item.assets.videoUrl && (
                                    <Box 
                                      sx={{ 
                                        position: 'absolute', 
                                        top: 12, 
                                        right: 12,
                                        backgroundColor: 'rgba(0,0,0,0.8)',
                                        borderRadius: '50%',
                                        p: 1
                                      }}
                                    >
                                      <VideoLibraryIcon sx={{ fontSize: 24, color: 'white' }} />
                                    </Box>
                                  )}
                                </Box>
                                <CardContent sx={{ p: 2 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">
                                      Design Type:
                                    </Typography>
                                    <Chip 
                                      label={item.assets.designType} 
                                      variant="outlined" 
                                      size="small"
                                    />
                                  </Box>
                                  {item.assets.videoUrl && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                      <VideoLibraryIcon color="primary" sx={{ fontSize: 16 }} />
                                      <Typography variant="body2" color="primary">
                                        Video Available
                                      </Typography>
                                    </Box>
                                  )}
                                </CardContent>
                              </Card>
                            </Box>
                          )}

                          {/* Prompts Preview (for Styles) - Mobile-like Horizontal Scroll */}
                          {item.prompts && item.prompts.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Prompts ({item.prompts.length}):
                              </Typography>
                              <Box 
                                sx={{ 
                                  display: 'flex', 
                                  gap: 2, 
                                  overflowX: 'auto',
                                  pb: 1,
                                  '&::-webkit-scrollbar': {
                                    height: 6,
                                  },
                                  '&::-webkit-scrollbar-track': {
                                    backgroundColor: '#f1f1f1',
                                    borderRadius: 3,
                                  },
                                  '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: '#c1c1c1',
                                    borderRadius: 3,
                                  },
                                  '&::-webkit-scrollbar-thumb:hover': {
                                    backgroundColor: '#a8a8a8',
                                  },
                                }}
                              >
                                {item.prompts.map((prompt, promptIndex) => (
                                  <Card 
                                    key={prompt.id || promptIndex}
                                    sx={{ 
                                      minWidth: 200,
                                      maxWidth: 200,
                                      flexShrink: 0,
                                      border: '1px solid #e0e0e0',
                                      borderRadius: 2,
                                      overflow: 'hidden'
                                    }}
                                  >
                                    <Box 
                                      sx={{ 
                                        height: 120, 
                                        backgroundColor: '#f5f5f5',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        position: 'relative'
                                      }}
                                    >
                                      {prompt.assets?.banner ? (
                                        <img 
                                          src={prompt.assets.banner} 
                                          alt={prompt.name || `Prompt ${promptIndex + 1}`}
                                          style={{ 
                                            width: '100%', 
                                            height: '100%', 
                                            objectFit: 'cover' 
                                          }}
                                        />
                                      ) : (
                                        <ImageIcon sx={{ fontSize: 40, color: '#ccc' }} />
                                      )}
                                      {prompt.assets?.videoUrl && (
                                        <Box 
                                          sx={{ 
                                            position: 'absolute', 
                                            top: 8, 
                                            right: 8,
                                            backgroundColor: 'rgba(0,0,0,0.7)',
                                            borderRadius: '50%',
                                            p: 0.5
                                          }}
                                        >
                                          <VideoLibraryIcon sx={{ fontSize: 16, color: 'white' }} />
                                        </Box>
                                      )}
                                    </Box>
                                    <CardContent sx={{ p: 1.5 }}>
                                      <Typography 
                                        variant="body2" 
                                        sx={{ 
                                          fontWeight: 'bold',
                                          mb: 0.5,
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap'
                                        }}
                                      >
                                        {prompt.name || `Prompt ${promptIndex + 1}`}
                                      </Typography>
                                      <Chip 
                                        label={prompt.assets?.designType || 'MEDIUM'} 
                                        size="small" 
                                        variant="outlined"
                                        sx={{ fontSize: '0.7rem' }}
                                      />
                                    </CardContent>
                                  </Card>
                                ))}
                              </Box>
                            </Box>
                          )}

                          {/* Styles Preview (for Sections) - Mobile-like Horizontal Scroll */}
                          {item.styles && item.styles.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Styles ({item.styles.length}):
                              </Typography>
                              <Box 
                                sx={{ 
                                  display: 'flex', 
                                  gap: 2, 
                                  overflowX: 'auto',
                                  pb: 1,
                                  '&::-webkit-scrollbar': {
                                    height: 6,
                                  },
                                  '&::-webkit-scrollbar-track': {
                                    backgroundColor: '#f1f1f1',
                                    borderRadius: 3,
                                  },
                                  '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: '#c1c1c1',
                                    borderRadius: 3,
                                  },
                                  '&::-webkit-scrollbar-thumb:hover': {
                                    backgroundColor: '#a8a8a8',
                                  },
                                }}
                              >
                                {item.styles.map((style, styleIndex) => (
                                  <Card 
                                    key={style.id || styleIndex}
                                    sx={{ 
                                      minWidth: 200,
                                      maxWidth: 200,
                                      flexShrink: 0,
                                      border: '1px solid #e0e0e0',
                                      borderRadius: 2,
                                      overflow: 'hidden'
                                    }}
                                  >
                                    <Box 
                                      sx={{ 
                                        height: 120, 
                                        backgroundColor: '#f5f5f5',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        position: 'relative'
                                      }}
                                    >
                                      {style.assets?.banner ? (
                                        <img 
                                          src={style.assets.banner} 
                                          alt={style.name || `Style ${styleIndex + 1}`}
                                          style={{ 
                                            width: '100%', 
                                            height: '100%', 
                                            objectFit: 'cover' 
                                          }}
                                        />
                                      ) : style.gifUrl ? (
                                        <img 
                                          src={style.gifUrl} 
                                          alt={style.name || `Style ${styleIndex + 1}`}
                                          style={{ 
                                            width: '100%', 
                                            height: '100%', 
                                            objectFit: 'cover' 
                                          }}
                                        />
                                      ) : (
                                        <BrushIcon sx={{ fontSize: 40, color: '#ccc' }} />
                                      )}
                                      {style.assets?.videoUrl && (
                                        <Box 
                                          sx={{ 
                                            position: 'absolute', 
                                            top: 8, 
                                            right: 8,
                                            backgroundColor: 'rgba(0,0,0,0.7)',
                                            borderRadius: '50%',
                                            p: 0.5
                                          }}
                                        >
                                          <VideoLibraryIcon sx={{ fontSize: 16, color: 'white' }} />
                                        </Box>
                                      )}
                                    </Box>
                                    <CardContent sx={{ p: 1.5 }}>
                                      <Typography 
                                        variant="body2" 
                                        sx={{ 
                                          fontWeight: 'bold',
                                          mb: 0.5,
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap'
                                        }}
                                      >
                                        {style.name || `Style ${styleIndex + 1}`}
                                      </Typography>
                                      <Chip 
                                        label={style.assets?.designType || style.designType || 'MEDIUM'} 
                                        size="small" 
                                        variant="outlined"
                                        sx={{ fontSize: '0.7rem' }}
                                      />
                                    </CardContent>
                                  </Card>
                                ))}
                              </Box>
                            </Box>
                          )}

                          {/* Tools Preview (for Sections) - Mobile-like Horizontal Scroll */}
                          {item.tools && item.tools.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Tools ({item.tools.length}):
                              </Typography>
                              <Box 
                                sx={{ 
                                  display: 'flex', 
                                  gap: 2, 
                                  overflowX: 'auto',
                                  pb: 1,
                                  '&::-webkit-scrollbar': {
                                    height: 6,
                                  },
                                  '&::-webkit-scrollbar-track': {
                                    backgroundColor: '#f1f1f1',
                                    borderRadius: 3,
                                  },
                                  '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: '#c1c1c1',
                                    borderRadius: 3,
                                  },
                                  '&::-webkit-scrollbar-thumb:hover': {
                                    backgroundColor: '#a8a8a8',
                                  },
                                }}
                              >
                                {item.tools.map((tool, toolIndex) => (
                                  <Card 
                                    key={tool.id || toolIndex}
                                    sx={{ 
                                      minWidth: 200,
                                      maxWidth: 200,
                                      flexShrink: 0,
                                      border: '1px solid #e0e0e0',
                                      borderRadius: 2,
                                      overflow: 'hidden'
                                    }}
                                  >
                                    <Box 
                                      sx={{ 
                                        height: 120, 
                                        backgroundColor: '#f5f5f5',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        position: 'relative'
                                      }}
                                    >
                                      {tool.banner ? (
                                        <img 
                                          src={tool.banner} 
                                          alt={tool.name || `Tool ${toolIndex + 1}`}
                                          style={{ 
                                            width: '100%', 
                                            height: '100%', 
                                            objectFit: 'cover' 
                                          }}
                                        />
                                      ) : tool.promptSvg ? (
                                        <img 
                                          src={tool.promptSvg} 
                                          alt={tool.name || `Tool ${toolIndex + 1}`}
                                          style={{ 
                                            width: '100%', 
                                            height: '100%', 
                                            objectFit: 'cover' 
                                          }}
                                        />
                                      ) : (
                                        <ViewModuleIcon sx={{ fontSize: 40, color: '#ccc' }} />
                                      )}
                                      {tool.videoUrl && (
                                        <Box 
                                          sx={{ 
                                            position: 'absolute', 
                                            top: 8, 
                                            right: 8,
                                            backgroundColor: 'rgba(0,0,0,0.7)',
                                            borderRadius: '50%',
                                            p: 0.5
                                          }}
                                        >
                                          <VideoLibraryIcon sx={{ fontSize: 16, color: 'white' }} />
                                        </Box>
                                      )}
                                    </Box>
                                    <CardContent sx={{ p: 1.5 }}>
                                      <Typography 
                                        variant="body2" 
                                        sx={{ 
                                          fontWeight: 'bold',
                                          mb: 0.5,
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap'
                                        }}
                                      >
                                        {tool.name || `Tool ${toolIndex + 1}`}
                                      </Typography>
                                      <Chip 
                                        label={tool.designType || 'MEDIUM'} 
                                        size="small" 
                                        variant="outlined"
                                        sx={{ fontSize: '0.7rem' }}
                                      />
                                    </CardContent>
                                  </Card>
                                ))}
                              </Box>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Alert severity="warning">
                  <Typography variant="body2">
                    No content found for this tag. Add some explore rows and content assets to see the preview.
                  </Typography>
                </Alert>
              )}
            </Box>
          )}
        </TabPanel>
      </Paper>

      {/* Add Content Modal */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <PlaylistAddIcon />
            Add "{selectedContent?.name}" to Explore
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
            <Alert severity="info">
              Choose how you want to add this content to the explore page:
            </Alert>

            <Box>
              <Typography variant="h6" gutterBottom>
                Content Details:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Chip label={selectedContent?.contentType} color="primary" size="small" />
                <Typography variant="body2">
                  Tag: {tags.find(t => t.id === selectedTag)?.name}
                </Typography>
              </Box>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" gutterBottom>
                Platform Selection:
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select which platforms this content should be visible on
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={customAssets.platform?.includes('IOS')}
                      onChange={(e) => {
                        const newPlatform = e.target.checked
                          ? [...(customAssets.platform || []), 'IOS']
                          : (customAssets.platform || []).filter(p => p !== 'IOS')
                        setCustomAssets(prev => ({ ...prev, platform: newPlatform }))
                      }}
                      icon={<AppleIcon />}
                      checkedIcon={<AppleIcon />}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AppleIcon />
                      <Typography>iOS</Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={customAssets.platform?.includes('ANDROID')}
                      onChange={(e) => {
                        const newPlatform = e.target.checked
                          ? [...(customAssets.platform || []), 'ANDROID']
                          : (customAssets.platform || []).filter(p => p !== 'ANDROID')
                        setCustomAssets(prev => ({ ...prev, platform: newPlatform }))
                      }}
                      icon={<AndroidIcon />}
                      checkedIcon={<AndroidIcon />}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AndroidIcon />
                      <Typography>Android</Typography>
                    </Box>
                  }
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                {customAssets.platform?.includes('IOS') && (
                  <Chip
                    icon={<AppleIcon />}
                    label="iOS"
                    size="small"
                    sx={{
                      backgroundColor: '#000',
                      color: '#fff',
                      '& .MuiChip-icon': { color: '#fff' }
                    }}
                  />
                )}
                {customAssets.platform?.includes('ANDROID') && (
                  <Chip
                    icon={<AndroidIcon />}
                    label="Android"
                    size="small"
                    sx={{
                      backgroundColor: '#3DDC84',
                      color: '#000',
                      '& .MuiChip-icon': { color: '#000' }
                    }}
                  />
                )}
                {!customAssets.platform?.length && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No platforms selected
                  </Typography>
                )}
              </Box>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" gutterBottom>
                Custom Assets (Optional):
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  You can either enter URLs directly or choose files to upload. Files will be uploaded automatically when you click "Add with Custom Assets".
                </Typography>
              </Alert>
              
              {/* Banner Section */}
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Banner Image:
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-end' }}>
                <TextField
                  label="Banner URL"
                  value={customAssets.banner}
                  onChange={(e) => setCustomAssets(prev => ({ ...prev, banner: e.target.value }))}
                  placeholder="https://example.com/banner.jpg"
                  sx={{ flex: 1 }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mx: 1, mb: 1 }}>
                  OR
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="banner-upload"
                    type="file"
                    onChange={(e) => handleFileSelect('banner', e)}
                  />
                  <label htmlFor="banner-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<CloudUploadIcon />}
                      size="small"
                    >
                      Choose Image
                    </Button>
                  </label>
                </Box>
              </Box>
              
              {selectedFiles.banner && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  Selected: {selectedFiles.banner.name}
                </Typography>
              )}
              
              {/* Video Section */}
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Video:
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-end' }}>
                <TextField
                  label="Video URL"
                  value={customAssets.videoUrl}
                  onChange={(e) => setCustomAssets(prev => ({ ...prev, videoUrl: e.target.value }))}
                  placeholder="https://example.com/video.mp4"
                  sx={{ flex: 1 }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mx: 1, mb: 1 }}>
                  OR
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <input
                    accept="video/*"
                    style={{ display: 'none' }}
                    id="video-upload"
                    type="file"
                    onChange={(e) => handleFileSelect('video', e)}
                  />
                  <label htmlFor="video-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<VideoLibraryIcon />}
                      size="small"
                    >
                      Choose Video
                    </Button>
                  </label>
                </Box>
              </Box>
              
              {selectedFiles.video && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  Selected: {selectedFiles.video.name}
                </Typography>
              )}
              
              {/* GIF Section */}
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                GIF:
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-end' }}>
                <TextField
                  label="GIF URL"
                  value={customAssets.gifUrl}
                  onChange={(e) => setCustomAssets(prev => ({ ...prev, gifUrl: e.target.value }))}
                  placeholder="https://example.com/animation.gif"
                  sx={{ flex: 1 }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mx: 1, mb: 1 }}>
                  OR
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <input
                    accept="image/gif"
                    style={{ display: 'none' }}
                    id="gif-upload"
                    type="file"
                    onChange={(e) => handleFileSelect('gif', e)}
                  />
                  <label htmlFor="gif-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<ImageIcon />}
                      size="small"
                    >
                      Choose GIF
                    </Button>
                  </label>
                </Box>
              </Box>
              
              {selectedFiles.gif && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  Selected: {selectedFiles.gif.name}
                </Typography>
              )}
              
              {/* Second Banner Section */}
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Second Banner:
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-end' }}>
                <TextField
                  label="Second Banner URL"
                  value={customAssets.secondBanner}
                  onChange={(e) => setCustomAssets(prev => ({ ...prev, secondBanner: e.target.value }))}
                  placeholder="https://example.com/second-banner.jpg"
                  sx={{ flex: 1 }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mx: 1, mb: 1 }}>
                  OR
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="second-banner-upload"
                    type="file"
                    onChange={(e) => handleFileSelect('secondBanner', e)}
                  />
                  <label htmlFor="second-banner-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<CloudUploadIcon />}
                      size="small"
                    >
                      Choose Image
                    </Button>
                  </label>
                </Box>
              </Box>
              
              {selectedFiles.secondBanner && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  Selected: {selectedFiles.secondBanner.name}
                </Typography>
              )}
              
              {/* WEBM Section */}
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                WEBM Video:
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-end' }}>
                <TextField
                  label="WEBM URL"
                  value={customAssets.webmUrl}
                  onChange={(e) => setCustomAssets(prev => ({ ...prev, webmUrl: e.target.value }))}
                  placeholder="https://example.com/video.webm"
                  sx={{ flex: 1 }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mx: 1, mb: 1 }}>
                  OR
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <input
                    accept="video/webm"
                    style={{ display: 'none' }}
                    id="webm-upload"
                    type="file"
                    onChange={(e) => handleFileSelect('webm', e)}
                  />
                  <label htmlFor="webm-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<VideoLibraryIcon />}
                      size="small"
                    >
                      Choose WEBM
                    </Button>
                  </label>
                </Box>
              </Box>
              
              {selectedFiles.webm && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  Selected: {selectedFiles.webm.name}
                </Typography>
              )}
              
              {/* WEBP Section */}
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                WEBP Image:
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-end' }}>
                <TextField
                  label="WEBP URL"
                  value={customAssets.webpUrl}
                  onChange={(e) => setCustomAssets(prev => ({ ...prev, webpUrl: e.target.value }))}
                  placeholder="https://example.com/image.webp"
                  sx={{ flex: 1 }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mx: 1, mb: 1 }}>
                  OR
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <input
                    accept="image/webp"
                    style={{ display: 'none' }}
                    id="webp-upload"
                    type="file"
                    onChange={(e) => handleFileSelect('webp', e)}
                  />
                  <label htmlFor="webp-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<CloudUploadIcon />}
                      size="small"
                    >
                      Choose WEBP
                    </Button>
                  </label>
                </Box>
              </Box>
              
              {selectedFiles.webp && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  Selected: {selectedFiles.webp.name}
                </Typography>
              )}
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Design Type</InputLabel>
                <Select
                  value={customAssets.designType}
                  label="Design Type"
                  onChange={(e) => setCustomAssets(prev => ({ ...prev, designType: e.target.value }))}
                >
                  <MenuItem value="SMALL">Small</MenuItem>
                  <MenuItem value="MEDIUM">Medium</MenuItem>
                  <MenuItem value="LARGE">Large</MenuItem>
                  <MenuItem value="BANNER">Banner</MenuItem>
                  <MenuItem value="VIDEO">Video</MenuItem>
                  <MenuItem value="CIRCLE">Circle</MenuItem>
                  <MenuItem value="PORTRAIT">Portrait</MenuItem>
                  <MenuItem value="SQUARE">Square</MenuItem>
                </Select>
              </FormControl>
              
              <TextField
                label="Row Number"
                type="number"
                value={customAssets.rowNumber}
                onChange={(e) => setCustomAssets(prev => ({ ...prev, rowNumber: parseInt(e.target.value) || 1 }))}
                placeholder="1"
                fullWidth
                helperText="Order position for this content item"
                inputProps={{ min: 1 }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button 
            onClick={handleAddWithDefaults}
            disabled={progress}
            startIcon={progress ? <CircularProgress size={16} /> : null}
          >
            Use Default Assets
          </Button>
          <Button 
            variant="contained" 
            onClick={handleAddWithCustomAssets}
            disabled={progress}
            startIcon={progress ? <CircularProgress size={16} /> : null}
          >
            {progress ? 'Processing...' : 'Add with Custom Assets'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackBarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackBarOpen(false)}
      >
        <Alert onClose={() => setSnackBarOpen(false)} severity={snackBarSeverity}>
          {snackBarMessage}
        </Alert>
      </Snackbar>

      {/* Edit Modal */}
      <ExploreRowEditModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false)
          setSelectedRowId(null)
        }}
        exploreRowId={selectedRowId}
      />
    </Box>
  )
}
